import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { InsightMarkdownPipe } from '@frontend/shared';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { getFilterStateByKey } from '../../contributions/contributions-filter-states.constants';
import { DeliveryConversationComponent } from '../../contributions/delivery-conversation/delivery-conversation.component';
import { LoaderGlobeAnimationDetailComponent } from '../../loaders';
import { BreadcrumbService } from '../../navigation/breadcrumb/breadcrumb.service';
import { PageTitleService } from '../../navigation/title/title.service';
import { CommunityInput, CommunityInputError } from '../community-input.model';
import { CommunityInputService } from '../community-input.service';

/**
 * The work screen of a feature review: the instructions, then the reviewer's feedback, then
 * handing it in — the Ideas plan, decision 3.32.
 *
 * **The feedback is the delivery's own conversation**, the one the insight editor uses for
 * comments between a contributor and staff: only the reviewer and staff see it, and staff may
 * reply. An input's instructions may ask for feedback some other way, such as by email, so handing
 * in asks for nothing: it submits the delivery, and staff review what came.
 */
@Component({
  selector: 'multisite-input-review',
  templateUrl: './input-review.component.html',
  imports: [RouterModule, TranslocoModule, InsightMarkdownPipe, LoaderGlobeAnimationDetailComponent, DeliveryConversationComponent],
  providers: [provideTranslocoScope('inputs'), provideTranslocoScope('contributions')],
})
export class InputReviewComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  private inputs = inject(CommunityInputService);
  private pageTitleService = inject(PageTitleService);
  private breadcrumbService = inject(BreadcrumbService);

  input = signal<CommunityInput | null>(null);
  failure = signal<CommunityInputError | null>(null);
  actionFailure = signal<CommunityInputError | null>(null);
  loading = signal(false);
  handingIn = signal(false);

  readonly delivery = computed(() => this.input()?.myDelivery ?? null);
  readonly handedIn = computed(() => !!this.delivery()?.submittedAt);
  readonly myState = computed(() => {
    const state = this.delivery()?.state;
    return state ? getFilterStateByKey(state) ?? null : null;
  });

  private slug = this.route.snapshot.paramMap.get('slug') ?? '';
  private subscriptions: Subscription[] = [];

  constructor() {
    this.load();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  handIn(): void {
    const input = this.input();
    const delivery = this.delivery();

    if (!input || !delivery || this.handingIn() || this.handedIn()) {
      return;
    }

    this.handingIn.set(true);
    this.actionFailure.set(null);

    this.subscriptions.push(
      this.inputs
        .submitReview(input.slug, delivery.id)
        .pipe(finalize(() => this.handingIn.set(false)))
        .subscribe({
          next: (updated) => this.input.set(updated),
          error: (failure: CommunityInputError) => this.actionFailure.set(failure),
        }),
    );
  }

  private load(): void {
    this.loading.set(true);
    this.failure.set(null);

    this.subscriptions.push(
      this.inputs
        .getOne(this.slug)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: (input) => {
            this.input.set(input);
            this.pageTitleService.setTitle(input.name ?? '');
            // The crumb for `:slug`, which links back to the input's page, is named after it.
            this.breadcrumbService.setBreadcrumbFragment({ urlFragment: 'slug', fragmentName: input.name ?? '' });
          },
          error: (failure: CommunityInputError) => this.failure.set(failure),
        }),
    );
  }
}
